import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import Button from './ui/Button'
import toast from 'react-hot-toast'

interface AddGoldModalProps {
  isOpen: boolean
  onClose: () => void
}

const AddGoldModal: React.FC<AddGoldModalProps> = ({ isOpen, onClose }) => {
  const { addHolding, customPortfolios, loadCustomPortfolios, assignHoldingToPortfolio, loadPortfolio } = usePortfolioStore()
  
  const [formData, setFormData] = useState({
    weight_baht: '', // จำนวนบาททอง
    purchase_price: '', // ราคาที่ซื้อ per baht
    purchase_date: '',
    notes: '',
    portfolioId: '' as string
  })

  const [loading, setLoading] = useState(false)

  // Load custom portfolios on mount
  useEffect(() => {
    loadCustomPortfolios()
  }, [])

  // Auto-select Gold portfolio if exists
  useEffect(() => {
    const goldPortfolio = customPortfolios.find(p => p.name === 'ทองคำ' || p.port_type === 'port_gold')
    if (goldPortfolio && !formData.portfolioId) {
      setFormData(prev => ({ ...prev, portfolioId: goldPortfolio.id.toString() }))
    }
  }, [customPortfolios, formData.portfolioId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.weight_baht || !formData.purchase_price || !formData.purchase_date) {
      toast.error('Please fill in all required fields')
      return
    }
    
    if (!formData.portfolioId) {
      toast.error('Please select a portfolio')
      return
    }

    setLoading(true)
    try {
      // Generate a unique symbol for this gold purchase
      const goldSymbol = `GOLD-${Date.now()}`
      const goldName = `ทองคำ ${parseFloat(formData.weight_baht)} บาท`

      // Add the gold holding with gold port_api_type
      await addHolding({
        symbol: goldSymbol,
        name: goldName,
        port_api_type: 'gold',
        sector: 'precious-metals',
        shares: parseFloat(formData.weight_baht), // Using shares field to store baht weight
        purchase_price: parseFloat(formData.purchase_price),
        purchase_currency: 'THB', // Gold is always priced in THB
        fund_class: undefined,
        created_at: `${formData.purchase_date}T00:00:00.000Z`,
        portfolio_id: parseInt(formData.portfolioId)
      })
      
      // Then assign the newly created holding to the selected portfolio
      setTimeout(async () => {
        try {
          // Reload portfolio to get the new holding
          await loadPortfolio()
          const portfolio = usePortfolioStore.getState().portfolio
          
          // Find the newly added holding by symbol
          const newHolding = portfolio
            .filter(h => h.symbol === goldSymbol)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
          
          if (newHolding) {
            await assignHoldingToPortfolio(parseInt(formData.portfolioId), newHolding.id)
          }
        } catch (error) {
          console.error('Error assigning gold to portfolio:', error)
        }
      }, 1000)
      
      // Reset form
      setFormData({
        weight_baht: '',
        purchase_price: '',
        purchase_date: '',
        notes: '',
        portfolioId: ''
      })
      
      toast.success('Gold purchase added successfully!')
      onClose()
    } catch (error) {
      console.error('Error adding gold:', error)
      toast.error('Failed to add gold purchase')
    } finally {
      setLoading(false)
    }
  }

  const goldPortfolioOptions = customPortfolios
    .filter(p => p.name === 'ทองคำ' || p.port_type === 'port_gold' || p.name.includes('ทอง'))
    .map(portfolio => ({
      value: portfolio.id.toString(),
      label: portfolio.name,
      icon: portfolio.icon
    }))

  const calculateTotalValue = () => {
    if (!formData.purchase_price || !formData.weight_baht) return ''
    const price = parseFloat(formData.purchase_price)
    const weight = parseFloat(formData.weight_baht)
    const total = price * weight
    return `Total: ฿${total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="sheet fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-yellow-500">🥇</span>
                เพิ่มการซื้อทองคำ
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Portfolio Selection */}
              {goldPortfolioOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Select Gold Portfolio
                  </label>
                  <div className="overflow-x-auto">
                    <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
                      {goldPortfolioOptions.map((portfolio) => (
                        <label
                          key={portfolio.value}
                          className={`relative flex flex-col items-center justify-center p-3 border rounded-lg cursor-pointer transition-all flex-shrink-0 w-24 ${
                            formData.portfolioId === portfolio.value
                              ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="portfolio"
                            value={portfolio.value}
                            checked={formData.portfolioId === portfolio.value}
                            onChange={() => setFormData({ ...formData, portfolioId: portfolio.value })}
                            className="sr-only"
                          />
                          <span className="text-lg mb-1">{portfolio.icon}</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white text-center">
                            {portfolio.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {goldPortfolioOptions.length === 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    No Gold portfolio found. Please create a Gold portfolio first.
                  </p>
                </div>
              )}

              {/* Weight in Baht */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนบาททอง *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.weight_baht}
                  onChange={(e) => setFormData({ ...formData, weight_baht: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1.00"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  น้ำหนักทองคำในหน่วยบาท (1 บาท = 15.244 กรัม)
                </p>
              </div>

              {/* Purchase Price per Baht */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ราคาที่ซื้อ (บาท/บาททอง) *
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="25000"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  ราคาทองคำต่อ 1 บาท (ไม่รวมค่าขายและค่าแปรรูป)
                </p>
              </div>

              {/* Purchase Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  วันที่ซื้อ *
                </label>
                <input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  หมายเหตุ (ไม่บังคับ)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  rows={3}
                  placeholder="เช่น สร้อยคอ, แหวน, แท่งทอง..."
                />
              </div>

              {/* Total Value */}
              {formData.weight_baht && formData.purchase_price && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {calculateTotalValue()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ({parseFloat(formData.weight_baht) || 0} บาท × ฿{parseInt(formData.purchase_price) || 0})
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button variant="secondary" className="flex-1"
                  type="button"
                  onClick={onClose}
                >
                  ยกเลิก
                </Button>
                <Button className="flex-1"
                  type="submit"
                  loading={loading}
                  disabled={goldPortfolioOptions.length === 0}
                >
                  เพิ่มทองคำ
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default AddGoldModal
